import { RefObject, useEffect, useState } from 'react';

/**
 * Tracks whether an element is currently visible in the viewport via IntersectionObserver.
 * Used to pause offscreen videos and resume them when scrolled back into view.
 *
 * Defaults to `true` so playback isn't blocked before the observer attaches (and on SSR /
 * browsers without IntersectionObserver, where we keep the previous always-on behaviour).
 */
export default function useInView<T extends Element>(
  ref: RefObject<T>,
  { threshold = 0.3 }: { threshold?: number } = {},
) {
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return inView;
}
